import { AlertTriangle, Check, Gauge, ShieldAlert, Wind } from 'lucide-react';
import { useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { toast } from 'react-toastify';
import {
  PEAK_FLOW_DANGER_SIGN_DEFINITIONS,
  PEAK_FLOW_PREDICTED_REFERENCE_SOURCES,
  PEAK_FLOW_REFERENCE_OPTIONS,
  PEAK_FLOW_SYMPTOM_DEFINITIONS,
  calculatePredictedPeakFlowReference,
  formatPeakFlowPercent,
  formatPeakFlowValue,
  getPeakFlowZoneTone,
  normalizePeakFlowPayload,
  normalizePeakFlowValue,
  resolveSuggestedPeakFlowPredictedReferenceSourceKey,
  summarizePeakFlowPayload,
  type PeakFlowDangerSignKey,
  type PeakFlowPredictedReferenceSourceKey,
  type PeakFlowReferenceMode,
  type PeakFlowResultPayload,
  type PeakFlowSymptomKey,
} from '../peak-flow/peakFlowDomain';

type PeakFlowPatientContext = {
  birthDate?: string | null;
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
  identifier?: string | null;
};

interface PeakFlowTestProps {
  initial?: PeakFlowResultPayload | null;
  patientContext?: PeakFlowPatientContext;
  onCancel?: () => void;
  onNext: (result: PeakFlowResultPayload) => void;
}

export type { PeakFlowResultPayload };

const buildReadingInputs = (readings?: number[] | null): string[] =>
  Array.from({ length: 3 }, (_, index) =>
    readings?.[index] != null ? String(readings[index]).replace('.', ',') : '',
  );

const formatPatientSummary = (patientContext?: PeakFlowPatientContext): string => {
  const parts = [
    patientContext?.ageYears != null ? `${patientContext.ageYears} anos` : null,
    patientContext?.sex || null,
    patientContext?.weightKg != null ? `${patientContext.weightKg} kg` : null,
    patientContext?.heightCm != null ? `${patientContext.heightCm} cm` : null,
  ].filter(Boolean);

  return parts.length ? parts.join(' - ') : 'Dados antropométricos incompletos para esta avaliação.';
};

const ReadingField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <label className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-3 flex items-center gap-3">
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputMode="decimal"
        placeholder="Ex.: 420"
        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-lg font-bold text-slate-900 outline-none transition focus:border-primary-400 focus:bg-white"
      />
      <span className="text-sm font-medium text-slate-500">L/min</span>
    </div>
  </label>
);

const FlagGrid = <T extends string>({
  title,
  icon,
  description,
  definitions,
  values,
  onToggle,
  danger = false,
}: {
  title: string;
  icon: ReactNode;
  description: string;
  definitions: Array<{ key: T; label: string; description: string }>;
  values: Record<T, boolean>;
  onToggle: (key: T) => void;
  danger?: boolean;
}) => (
  <section className={`rounded-[28px] border p-6 shadow-sm ${danger ? 'border-rose-200 bg-rose-50/70' : 'border-slate-200 bg-white'}`}>
    <div className="flex items-start gap-4">
      <div className={`rounded-2xl p-3 ${danger ? 'bg-white text-rose-700' : 'bg-primary-50 text-primary-700'}`}>{icon}</div>
      <div>
        <h3 className="text-xl font-black tracking-tight text-slate-900">{title}</h3>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">{description}</p>
      </div>
    </div>

    <div className="mt-5 grid gap-3 md:grid-cols-2">
      {definitions.map((item) => {
        const active = values[item.key];
        return (
          <label
            key={item.key}
            className={`cursor-pointer rounded-[22px] border p-4 transition ${
              active
                ? danger
                  ? 'border-rose-300 bg-white shadow-sm'
                  : 'border-primary-300 bg-primary-50/70 shadow-sm'
                : 'border-slate-200 bg-slate-50/70 hover:border-slate-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={active}
                onChange={() => onToggle(item.key)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <div className="text-sm font-bold text-slate-900">{item.label}</div>
                <div className="mt-1 text-sm leading-relaxed text-slate-600">{item.description}</div>
              </div>
            </div>
          </label>
        );
      })}
    </div>
  </section>
);

const SummaryCard = ({
  label,
  value,
  detail,
  accent = 'text-slate-900',
}: {
  label: string;
  value: string;
  detail?: string | null;
  accent?: string;
}) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className={`mt-2 text-3xl font-black ${accent}`}>{value}</div>
    {detail ? <div className="mt-2 text-sm text-slate-500">{detail}</div> : null}
  </div>
);

export default function PeakFlowTest({ initial = null, patientContext, onCancel, onNext }: PeakFlowTestProps) {
  const normalizedInitial = normalizePeakFlowPayload(initial);
  const initialPredictedReferenceSourceKey =
    normalizedInitial?.referenceSourceKey ?? resolveSuggestedPeakFlowPredictedReferenceSourceKey(patientContext);

  const [preInputs, setPreInputs] = useState<string[]>(buildReadingInputs(normalizedInitial?.preReadings));
  const [postInputs, setPostInputs] = useState<string[]>(buildReadingInputs(normalizedInitial?.postReadings));
  const [referenceMode, setReferenceMode] = useState<PeakFlowReferenceMode>(normalizedInitial?.referenceMode ?? 'none');
  const [referenceSourceKey, setReferenceSourceKey] = useState<PeakFlowPredictedReferenceSourceKey>(initialPredictedReferenceSourceKey);
  const [referenceValue, setReferenceValue] = useState<string>(
    normalizedInitial?.referenceValue != null ? String(normalizedInitial.referenceValue).replace('.', ',') : '',
  );
  const [bronchodilatorUsed, setBronchodilatorUsed] = useState<boolean>(Boolean(normalizedInitial?.bronchodilatorUsed));
  const [bronchodilatorMedication, setBronchodilatorMedication] = useState(normalizedInitial?.bronchodilatorMedication ?? '');
  const [bronchodilatorWaitMinutes, setBronchodilatorWaitMinutes] = useState<string>(
    normalizedInitial?.bronchodilatorWaitMinutes != null
      ? String(normalizedInitial.bronchodilatorWaitMinutes).replace('.', ',')
      : '',
  );
  const [symptoms, setSymptoms] = useState<Record<PeakFlowSymptomKey, boolean>>({
    wheeze: Boolean(normalizedInitial?.symptoms?.wheeze),
    cough: Boolean(normalizedInitial?.symptoms?.cough),
    chestTightness: Boolean(normalizedInitial?.symptoms?.chestTightness),
    shortnessOfBreath: Boolean(normalizedInitial?.symptoms?.shortnessOfBreath),
    nocturnalSymptoms: Boolean(normalizedInitial?.symptoms?.nocturnalSymptoms),
    triggerExposure: Boolean(normalizedInitial?.symptoms?.triggerExposure),
    relieverUseToday: Boolean(normalizedInitial?.symptoms?.relieverUseToday),
  });
  const [dangerSigns, setDangerSigns] = useState<Record<PeakFlowDangerSignKey, boolean>>({
    troubleWalkingOrTalking: Boolean(normalizedInitial?.dangerSigns?.troubleWalkingOrTalking),
    lipsBlue: Boolean(normalizedInitial?.dangerSigns?.lipsBlue),
    severeBreathlessness: Boolean(normalizedInitial?.dangerSigns?.severeBreathlessness),
    rapidWorsening: Boolean(normalizedInitial?.dangerSigns?.rapidWorsening),
  });
  const [notes, setNotes] = useState(normalizedInitial?.notes ?? '');
  const [referenceValidationError, setReferenceValidationError] = useState<string | null>(null);
  const referenceInputRef = useRef<HTMLInputElement>(null);
  const storedPredictedReferenceValue =
    normalizedInitial?.referenceMode === 'predicted_reference' && initialPredictedReferenceSourceKey === referenceSourceKey
      ? normalizedInitial.referenceValue ?? null
      : null;

  const preReadings = useMemo(
    () => preInputs.map((item) => normalizePeakFlowValue(item)).filter((item): item is number => item != null),
    [preInputs],
  );
  const postReadings = useMemo(
    () => postInputs.map((item) => normalizePeakFlowValue(item)).filter((item): item is number => item != null),
    [postInputs],
  );
  const parsedReferenceValue = useMemo(() => normalizePeakFlowValue(referenceValue), [referenceValue]);
  const parsedWaitMinutes = useMemo(() => normalizePeakFlowValue(bronchodilatorWaitMinutes), [bronchodilatorWaitMinutes]);
  const predictedReference = useMemo(
    () => calculatePredictedPeakFlowReference(patientContext, referenceSourceKey),
    [patientContext, referenceSourceKey],
  );
  const effectiveReferenceValue = useMemo(() => {
    if (referenceMode === 'none') return null;
    if (referenceMode === 'predicted_reference') return predictedReference.predictedValue ?? storedPredictedReferenceValue;
    return parsedReferenceValue;
  }, [parsedReferenceValue, predictedReference.predictedValue, referenceMode, storedPredictedReferenceValue]);
  const displayedReferenceValue = useMemo(() => {
    if (referenceMode !== 'predicted_reference') return referenceValue;
    return effectiveReferenceValue != null ? String(effectiveReferenceValue).replace('.', ',') : '';
  }, [effectiveReferenceValue, referenceMode, referenceValue]);
  const referenceFieldLabel =
    referenceMode === 'personal_best'
      ? 'Melhor pessoal'
      : referenceMode === 'predicted_reference'
        ? 'Valor previsto'
        : 'Valor de referência';
  const referenceFieldPlaceholder =
    referenceMode === 'none'
      ? 'Não aplicável'
      : referenceMode === 'predicted_reference'
        ? 'Calculado automaticamente'
        : 'Ex.: 420';
  const predictedReferenceAvailable = effectiveReferenceValue != null;
  const manualReferenceRequired = referenceMode === 'personal_best' || referenceMode === 'manual_reference';

  const previewPayload: PeakFlowResultPayload = useMemo(
    () => ({
      preReadings,
      postReadings: bronchodilatorUsed ? postReadings : [],
      bronchodilatorUsed,
      bronchodilatorMedication: bronchodilatorMedication.trim() || null,
      bronchodilatorWaitMinutes: bronchodilatorUsed ? parsedWaitMinutes : null,
      referenceMode,
      referenceValue: effectiveReferenceValue,
      referenceLabel:
        referenceMode === 'personal_best'
          ? 'melhor pessoal'
          : referenceMode === 'predicted_reference'
            ? predictedReference.shortLabel
            : referenceMode === 'manual_reference'
              ? 'valor de referência'
            : null,
      referenceSourceKey: referenceMode === 'predicted_reference' ? referenceSourceKey : null,
      referenceSourceLabel: referenceMode === 'predicted_reference' ? predictedReference.sourceLabel : null,
      referencePopulationNote: referenceMode === 'predicted_reference' ? predictedReference.populationLabel : null,
      symptoms,
      dangerSigns,
      notes: notes.trim() || null,
      patientContext,
    }),
    [
      bronchodilatorMedication,
      bronchodilatorUsed,
      dangerSigns,
      effectiveReferenceValue,
      notes,
      parsedWaitMinutes,
      patientContext,
      predictedReference.shortLabel,
      predictedReference.populationLabel,
      predictedReference.sourceLabel,
      postReadings,
      preReadings,
      referenceSourceKey,
      referenceMode,
      symptoms,
    ],
  );
  const summary = useMemo(() => summarizePeakFlowPayload(previewPayload), [previewPayload]);

  const updateReadingInput = (setter: Dispatch<SetStateAction<string[]>>, index: number, value: string) => {
    setter((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  const handleFinish = () => {
    setReferenceValidationError(null);

    if (preReadings.length !== 3) {
      toast.error('Registre 3 manobras pré-broncodilatador válidas para concluir.');
      return;
    }

    if (referenceMode === 'predicted_reference' && effectiveReferenceValue == null) {
      toast.error(predictedReference.unavailableReason || 'Não foi possível calcular o valor previsto automático.');
      return;
    }

    if (referenceMode !== 'none' && referenceMode !== 'predicted_reference' && effectiveReferenceValue == null) {
      const fieldName = referenceMode === 'personal_best' ? 'Melhor pessoal' : 'Valor de referência';
      const message = `Preencha o campo obrigatório “${fieldName}” com um valor maior que zero antes de salvar.`;
      setReferenceValidationError(message);
      toast.error(message);
      referenceInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      referenceInputRef.current?.focus({ preventScroll: true });
      return;
    }

    if (bronchodilatorUsed && postReadings.length !== 3) {
      toast.error('Quando a etapa pós-broncodilatador for utilizada, registre 3 manobras válidas.');
      return;
    }

    onNext({
      ...previewPayload,
      interpretation: summary.interpretation,
      referenceNote: summary.referenceNote,
      finishedAt: new Date().toISOString(),
      professionalContext: normalizedInitial?.professionalContext,
    });
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <div className="text-[11px] font-black uppercase tracking-[0.24em] text-primary-700">Pico de fluxo expiratório</div>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950">Triagem funcional de fluxo expiratório</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              Registre 3 manobras pré-broncodilatador. A etapa pós-broncodilatador é opcional nesta fase, mas, quando usada,
              deve conter 3 medidas para comparação objetiva.
            </p>
            <p className="mt-3 text-sm font-medium text-slate-600">{formatPatientSummary(patientContext)}</p>
          </div>

          <div className="rounded-[26px] border border-white/70 bg-white/80 p-5 shadow-sm lg:w-[320px]">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Leitura operacional</div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className={`rounded-full px-4 py-2 text-xs font-bold shadow-sm ${summary.severityTone}`}>{summary.severity}</span>
              <span className={`rounded-full px-4 py-2 text-xs font-bold shadow-sm ${getPeakFlowZoneTone(previewPayload)}`}>{summary.zoneLabel}</span>
            </div>
            <div className="mt-4 text-4xl font-black" style={{ color: summary.scoreColor }}>
              {summary.bestPreFlow != null ? formatPeakFlowValue(summary.bestPreFlow) : '—'}
            </div>
            <div className="text-sm font-medium text-slate-500">melhor valor pré (L/min)</div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">{summary.interpretation}</p>
          </div>
        </div>
      </section>

      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="rounded-2xl bg-primary-50 p-3 text-primary-700">
            <Gauge className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-xl font-black tracking-tight text-slate-900">Referência clínica</h3>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">
              Use melhor pessoal quando o paciente já tiver acompanhamento. No atendimento inicial, a clínica pode registrar uma
              referência manual ou usar o valor previsto automático quando os dados do paciente forem compatíveis com o estudo.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <label className="rounded-[24px] border border-slate-200 bg-slate-50/80 p-4">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Modo de referência</div>
            <select
              value={referenceMode}
              onChange={(event) => {
                setReferenceMode(event.target.value as PeakFlowReferenceMode);
                setReferenceValidationError(null);
              }}
              className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-primary-400"
            >
              {PEAK_FLOW_REFERENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="mt-2 text-sm text-slate-500">
              {PEAK_FLOW_REFERENCE_OPTIONS.find((option) => option.value === referenceMode)?.description}
            </div>
          </label>

          <div
            className={`rounded-[24px] border p-4 transition ${
              referenceValidationError ? 'border-rose-300 bg-rose-50/60' : 'border-slate-200 bg-slate-50/80'
            }`}
          >
            <label
              htmlFor="peak-flow-reference-value"
              className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400"
            >
              {referenceFieldLabel}
              {manualReferenceRequired ? <span className="ml-1 text-rose-600">*</span> : null}
            </label>
            <div className="mt-3 flex items-center gap-3">
              <input
                id="peak-flow-reference-value"
                ref={referenceInputRef}
                value={displayedReferenceValue}
                onChange={(event) => {
                  setReferenceValue(event.target.value);
                  setReferenceValidationError(null);
                }}
                disabled={referenceMode === 'none' || referenceMode === 'predicted_reference'}
                required={manualReferenceRequired}
                aria-invalid={Boolean(referenceValidationError)}
                aria-describedby={manualReferenceRequired ? 'peak-flow-reference-requirement' : undefined}
                inputMode="decimal"
                placeholder={referenceFieldPlaceholder}
                className={`w-full rounded-2xl border bg-white px-4 py-3 text-lg font-bold text-slate-900 outline-none transition disabled:bg-slate-100 disabled:text-slate-400 ${
                  referenceValidationError
                    ? 'border-rose-400 focus:border-rose-500 focus:ring-2 focus:ring-rose-100'
                    : 'border-slate-200 focus:border-primary-400'
                }`}
              />
              <span className="text-sm font-medium text-slate-500">L/min</span>
            </div>
            {manualReferenceRequired ? (
              <div
                id="peak-flow-reference-requirement"
                role={referenceValidationError ? 'alert' : undefined}
                className={`mt-3 flex items-start gap-2 text-sm ${
                  referenceValidationError ? 'font-semibold text-rose-700' : 'text-slate-500'
                }`}
              >
                {referenceValidationError ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> : null}
                <span>
                  {referenceValidationError ?? `Campo obrigatório para a opção “${referenceFieldLabel}”.`}
                </span>
              </div>
            ) : null}
            {referenceMode === 'predicted_reference' ? (
              <div
                className={`mt-3 rounded-2xl border px-4 py-3 text-sm leading-relaxed ${
                  predictedReferenceAvailable
                    ? 'border-primary-200 bg-primary-50/70 text-primary-900'
                    : 'border-amber-200 bg-amber-50/80 text-amber-900'
                }`}
              >
                <label className="block">
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">Fonte automática</div>
                  <select
                    value={referenceSourceKey}
                    onChange={(event) => setReferenceSourceKey(event.target.value as PeakFlowPredictedReferenceSourceKey)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-primary-400"
                  >
                    {PEAK_FLOW_PREDICTED_REFERENCE_SOURCES.map((source) => (
                      <option key={source.key} value={source.key}>
                        {source.label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="mt-3 font-bold">{predictedReference.sourceLabel}</div>
                <div className="mt-1">
                  Base populacional: {predictedReference.populationLabel}.
                </div>
                <div className="mt-1">
                  Requisitos: {predictedReference.requirementsLabel}. Faixas publicadas da referência: estatura{' '}
                  {predictedReference.heightRangeLabel}
                  {predictedReference.ageRangeLabel ? ` • idade ${predictedReference.ageRangeLabel}` : ''}.
                </div>
                <div className="mt-1">
                  {predictedReferenceAvailable
                    ? 'Valor previsto calculado com os dados do cadastro atual e a fonte selecionada.'
                    : predictedReference.unavailableReason || 'Não foi possível calcular o valor previsto automático.'}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="rounded-2xl bg-primary-50 p-3 text-primary-700">
            <Wind className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-xl font-black tracking-tight text-slate-900">Manobras registradas</h3>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">
              Registre a melhor de 3 manobras. Se optar por repetir após broncodilatador, registre novamente 3 medidas.
            </p>
          </div>
        </div>

        <div className="mt-5">
          <div className="mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Pré-broncodilatador</div>
          <div className="grid gap-4 lg:grid-cols-3">
            {preInputs.map((value, index) => (
              <ReadingField
                key={`pre-reading-${index}`}
                label={`${index + 1}a manobra`}
                value={value}
                onChange={(nextValue) => updateReadingInput(setPreInputs, index, nextValue)}
              />
            ))}
          </div>
        </div>

        <div className="mt-6 rounded-[24px] border border-slate-200 bg-slate-50/80 p-5">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={bronchodilatorUsed}
              onChange={(event) => setBronchodilatorUsed(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <div className="text-sm font-bold text-slate-900">Executar etapa pós-broncodilatador</div>
              <div className="mt-1 text-sm leading-relaxed text-slate-600">
                Use quando fizer sentido no atendimento inicial. O resultado final vai calcular o ganho absoluto e percentual.
              </div>
            </div>
          </label>

          {bronchodilatorUsed ? (
            <div className="mt-5 space-y-4">
              <div className="grid gap-4 lg:grid-cols-2">
                <label className="rounded-[22px] border border-slate-200 bg-white p-4">
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Broncodilatador</div>
                  <input
                    value={bronchodilatorMedication}
                    onChange={(event) => setBronchodilatorMedication(event.target.value)}
                    placeholder="Ex.: salbutamol"
                    className="mt-3 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-primary-400 focus:bg-white"
                  />
                </label>

                <label className="rounded-[22px] border border-slate-200 bg-white p-4">
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Intervalo após uso</div>
                  <div className="mt-3 flex items-center gap-3">
                    <input
                      value={bronchodilatorWaitMinutes}
                      onChange={(event) => setBronchodilatorWaitMinutes(event.target.value)}
                      inputMode="decimal"
                      placeholder="Ex.: 15"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-900 outline-none transition focus:border-primary-400 focus:bg-white"
                    />
                    <span className="text-sm font-medium text-slate-500">min</span>
                  </div>
                </label>
              </div>

              <div>
                <div className="mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Pós-broncodilatador</div>
                <div className="grid gap-4 lg:grid-cols-3">
                  {postInputs.map((value, index) => (
                    <ReadingField
                      key={`post-reading-${index}`}
                      label={`${index + 1}a manobra`}
                      value={value}
                      onChange={(nextValue) => updateReadingInput(setPostInputs, index, nextValue)}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <FlagGrid
        title="Sintomas e contexto clínico"
        icon={<Check className="h-6 w-6" />}
        description="Esses marcadores ajudam a interpretar o PEF dentro do quadro clínico, sem transformar a medida isolada em diagnóstico."
        definitions={PEAK_FLOW_SYMPTOM_DEFINITIONS}
        values={symptoms}
        onToggle={(key) => setSymptoms((current) => ({ ...current, [key]: !current[key] }))}
      />

      <FlagGrid
        title="Sinais de alarme"
        icon={<ShieldAlert className="h-6 w-6" />}
        description="Marque apenas quando houver gravidade observada ou referida no momento da avaliação."
        definitions={PEAK_FLOW_DANGER_SIGN_DEFINITIONS}
        values={dangerSigns}
        onToggle={(key) => setDangerSigns((current) => ({ ...current, [key]: !current[key] }))}
        danger
      />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Interpretação automática</div>
          <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-900">{summary.actionLabel}</h3>
          <p className="mt-4 text-sm leading-relaxed text-slate-600">{summary.interpretation}</p>

          <label className="mt-6 block rounded-[22px] border border-slate-200 bg-slate-50/80 p-4">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Observações do teste</div>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={5}
              placeholder="Observações complementares do atendimento, técnica, tolerância ou contexto clínico."
              className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-900 outline-none transition focus:border-primary-400"
            />
          </label>
        </div>

        <div className="space-y-4">
          <SummaryCard
            label="Melhor pré"
            value={summary.bestPreFlow != null ? `${formatPeakFlowValue(summary.bestPreFlow)} L/min` : '—'}
            detail={`${summary.preAttemptCount}/3 manobras válidas`}
            accent="text-slate-900"
          />
          {summary.bestPostFlow != null ? (
            <SummaryCard
              label="Melhor pós-broncodilatador"
              value={`${formatPeakFlowValue(summary.bestPostFlow)} L/min`}
              detail={`${summary.postAttemptCount}/3 manobras válidas`}
              accent="text-primary-700"
            />
          ) : null}
          {summary.referencePercentPre != null ? (
            <SummaryCard
              label="% da referência"
              value={formatPeakFlowPercent(summary.referencePercentPre)}
              detail={`${summary.zoneLabel} • ${summary.referenceLabel}`}
              accent="text-slate-900"
            />
          ) : null}
          {summary.bronchodilatorDeltaPercent != null ? (
            <SummaryCard
              label="Variação após broncodilatador"
              value={formatPeakFlowPercent(summary.bronchodilatorDeltaPercent)}
              detail={
                summary.bronchodilatorDeltaAbsolute != null
                  ? `${formatPeakFlowValue(summary.bronchodilatorDeltaAbsolute)} L/min`
                  : null
              }
              accent="text-primary-700"
            />
          ) : null}
        </div>
      </section>

      {dangerSigns.troubleWalkingOrTalking ||
      dangerSigns.lipsBlue ||
      dangerSigns.severeBreathlessness ||
      dangerSigns.rapidWorsening ? (
        <div className="rounded-[24px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm leading-relaxed text-rose-900 shadow-sm">
          <div className="flex items-center gap-3 font-bold">
            <AlertTriangle className="h-5 w-5" />
            Sinais de alarme registrados
          </div>
          <p className="mt-2">
            Este registro deve ser interpretado com prioridade clínica. O sistema mantém a rastreabilidade da medida, mas a
            decisão assistencial continua dependente da avaliação profissional.
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full border border-slate-200 px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
          >
            Cancelar
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleFinish}
          className="rounded-full bg-primary-700 px-6 py-3 text-sm font-black text-white shadow-lg shadow-primary-200 transition hover:bg-primary-800"
        >
          Salvar Peak Flow
        </button>
      </div>
    </div>
  );
}
